// Customer Purchase Order capture on a pipeline record.
//
// OPT-IN, DELIBERATELY. Most of RBM's business is repeat distributor orders
// confirmed over the phone, with no PO document anywhere. A PO number field
// that every record nagged about would just be filled with "NA", which is worse
// than not asking. So the rep ticks the box to say "this one is PO-backed", and
// only then does the number become required — enforced server-side at Order
// Confirmed (src/shared/sales/customer-po.js), not merely here.
//
// Shared by the New and Edit record forms and the record detail page, so the
// three cannot drift into asking for different things.

import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Upload, X, Paperclip, Loader2 } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Input } from '@/shared/components/ui/Input';

export interface CustomerPoAttachment {
  url: string;
  name: string;
}

export interface CustomerPoValue {
  hasCustomerPo: boolean;
  customerPoNumber: string;
  /** ISO yyyy-mm-dd, the value an <input type="date"> exchanges. */
  customerPoDate: string;
  customerPoAttachments: CustomerPoAttachment[];
}

export const EMPTY_CUSTOMER_PO: CustomerPoValue = {
  hasCustomerPo: false,
  customerPoNumber: '',
  customerPoDate: '',
  customerPoAttachments: [],
};

/** Normalises whatever the API returned onto the form's own shape. */
export function customerPoFromRecord(record: Record<string, unknown> | null | undefined): CustomerPoValue {
  if (!record) return { ...EMPTY_CUSTOMER_PO };
  const raw = (record.customerPoAttachments as CustomerPoAttachment[] | undefined) ?? [];
  const date = record.customerPoDate as string | undefined;
  return {
    hasCustomerPo: !!record.hasCustomerPo,
    customerPoNumber: (record.customerPoNumber as string) || '',
    // Trim an ISO timestamp back to the date half the input can display.
    customerPoDate: date ? String(date).slice(0, 10) : '',
    customerPoAttachments: raw.map((a) => ({ url: a.url, name: a.name })),
  };
}

/** Whether this record would be refused at Order Confirmed, for a local warning. */
export function isCustomerPoIncomplete(v: CustomerPoValue): boolean {
  return v.hasCustomerPo && v.customerPoNumber.trim().length === 0;
}

const MAX_FILES = 20;
const MAX_BYTES = 50 * 1024 * 1024; // Mirrors the server's own multer limit.

export function CustomerPoFields({
  value,
  onChange,
  disabled = false,
}: {
  value: CustomerPoValue;
  onChange: (next: CustomerPoValue) => void;
  disabled?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const set = <K extends keyof CustomerPoValue>(key: K, v: CustomerPoValue[K]) =>
    onChange({ ...value, [key]: v });

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_FILES - value.customerPoAttachments.length;
    if (room <= 0) {
      toast.error(`A record can hold ${MAX_FILES} PO documents.`);
      return;
    }

    setUploading(true);
    const added: CustomerPoAttachment[] = [];
    try {
      for (const file of Array.from(files).slice(0, room)) {
        if (file.size > MAX_BYTES) {
          toast.error(`"${file.name}" is larger than 50 MB.`);
          continue;
        }
        const body = new FormData();
        body.append('file', file);
        const { data } = await api.post<{ url: string }>('/api/upload', body);
        if (data?.url) added.push({ url: data.url, name: file.name });
      }
      // One state write for the whole batch: writing per file would drop
      // uploads, because each callback closes over the value it started with.
      if (added.length) {
        onChange({ ...value, customerPoAttachments: [...value.customerPoAttachments, ...added] });
        toast.success(added.length === 1 ? 'PO document attached.' : `${added.length} PO documents attached.`);
      }
    } catch {
      toast.error('The upload failed. The file was not attached.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removeAt = (index: number) =>
    set('customerPoAttachments', value.customerPoAttachments.filter((_, i) => i !== index));

  const incomplete = isCustomerPoIncomplete(value);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-outline bg-surface p-5 shadow-sm">
      <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
        <FileText className="h-4 w-4 text-primary" />
        <h2 className="text-body-base font-semibold text-on-surface">Customer Purchase Order</h2>
      </div>

      <label className="flex cursor-pointer items-start gap-2.5 select-none">
        <input
          type="checkbox"
          checked={value.hasCustomerPo}
          disabled={disabled}
          onChange={(e) => set('hasCustomerPo', e.target.checked)}
          className="mt-0.5 rounded border-input accent-primary"
        />
        <span>
          <span className="text-body-sm font-medium text-on-surface">This order has a customer PO</span>
          <span className="block text-body-xs text-on-surface-variant">
            Leave unticked for a phone or email confirmation with no formal PO document.
          </span>
        </span>
      </label>

      {value.hasCustomerPo && (
        <div className="flex flex-col gap-3.5 border-l-2 border-primary/30 pl-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="PO Number *"
              value={value.customerPoNumber}
              onChange={(e) => set('customerPoNumber', e.target.value)}
              placeholder="e.g. PO-4471"
              disabled={disabled}
            />
            <Input
              label="PO Date"
              type="date"
              value={value.customerPoDate}
              onChange={(e) => set('customerPoDate', e.target.value)}
              disabled={disabled}
            />
          </div>

          {incomplete && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-body-xs text-amber-800">
              The PO number is needed before this record can move to <strong>Order Confirmed</strong>.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-body-sm font-medium text-on-surface">PO Documents</span>
            {value.customerPoAttachments.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {value.customerPoAttachments.map((a, i) => (
                  <li
                    key={`${a.url}-${i}`}
                    className="flex items-center gap-2 rounded-lg border border-outline/70 bg-surface-container-low px-3 py-2"
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-on-surface-variant" />
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 flex-1 truncate text-body-xs font-medium text-primary hover:underline"
                    >
                      {a.name}
                    </a>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeAt(i)}
                        aria-label={`Remove ${a.name}`}
                        className="rounded p-0.5 text-on-surface-variant transition hover:bg-rose-50 hover:text-rose-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {!disabled && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                  className="inline-flex w-fit items-center gap-1.5 rounded-xl border border-outline bg-surface px-3 py-2 text-body-xs font-semibold text-on-surface transition hover:bg-surface-container-low disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploading ? 'Uploading…' : 'Attach PO document'}
                </button>
                {/* Amendments and revised POs arrive against the same order, so
                    the list grows rather than replacing what came before. */}
                <p className="text-body-xs text-on-surface-variant">
                  Attach the original PO plus any amendments or annexures. PDF or image, up to 50 MB each.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerPoFields;
